/**
 * validation-messages.component.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { Component, Input } from "@angular/core";
import { AbstractControl, FormArray, FormControl, FormGroup }
    from "@angular/forms";

// anything that turns the error value into a string
type MessageFn = (v : any) => string;

// a validation message is either a string or a value=>string callback
type VMessage = string | MessageFn;

// NOTE: validation levels are currently unused
type ValidationLevel = "standard" | "alert";

/**
 * {@link ValidationMessages} is a static helper class that assists with
 * validation errors, particularly in supplying human-readable warning messages.
 */
export class ValidationMessages
{
    /**
     * Default message to present when the error key is unrecognized.
     */
    public static UnknownError : string = "(NO VALIDATION MSG DEFINED)";

    /**
     * Map from error key strings to warning messages or callbacks that generate
     * warning messages from the error value.
     */
    public static Messages : Map<string, VMessage> =
        new Map([
            // --- stock validators ---
            ["required", "This field is required."],
            ["minLength", "This value is too short."],
            ["maxLength", "This value is too long."],
            // requiredTrue
            // pattern

            // --- custom validators ---
            ["integer", "The value must be an integer."],
            ["number", "The value must be a number."],
            ["min", "The value is less than the minimum."],
            ["max", "The value is greater than the maximum."],
            ["date", "The value is not a correctly formatted date."],
            ["boolean", "The value is not a a boolean (true/false)."],
            ["arrayMin", "The array must have a minimum number of values."],
            ["equalsTo", "The fields do not match."],
            ["safeInteger", (v : any) =>
                `The value is ${v} the range of 'safe' numbers.`],
            ["requiredList", "Empty values are not allowed."],
            ["onlyLastEmpty", "Only the last entry can be empty."],
            ["in", (v : any) => `The value "${v}" is not allowed.`],
            ["subset", "Not all values are in the allowed set."],
            ["noInternalNulls",
                (v : any) =>
                    `The value in row ${v + 1} is blank.
                    Only use "internal" blanks when completely necessary.`
            ]
        ] as Array<[string, VMessage]>);

    /**
     * Return a priority / logging level for this validation error.
     */
    public static getLevel (errorName : string) : ValidationLevel
    {
        return "standard";
    }

    /**
     * Whether a validation error shoud still allow submission of answers.
     */
    public static allowSubmit (errorName : string, value? : any) : boolean
    {
        if (errorName === "noInternalNulls") {
            return true;
        }
        return false;
    }

    /**
     * Whether an abstract form control and all its descendants allow
     * submission of answers.
     */
    public static submittable (control : AbstractControl) : boolean
    {
        if (control.errors
            && Object.keys(control.errors).some(
                (err : string) => !this.allowSubmit(err))) {
            return false;
        }

        if (control instanceof FormArray) {
            return control.controls.every(
                (c : AbstractControl) => this.submittable(c));
        }
        if (control instanceof FormGroup) {
            return Object.values(control.controls).every(
                (c : AbstractControl) => this.submittable(c));
        }
        return true;
    }

    /**
     * Get a human-readable warning for an error key-value pair.
     */
    public static getMessage (errorName : string, value? : any) : string
    {
        const msg: string | MessageFn =
            ValidationMessages.Messages.get(errorName);
        if (!msg) {
            return ValidationMessages.UnknownError;
        }
        if (typeof msg === "function") {
            return msg(value);
        }
        return msg;
    }
}

/**
 * The {@link ValidationMessagesComponent} is a simple template that displays
 * readable warnings for all of the errors of a provided control.
 */
@Component({
    selector: "validation-messages",
    template: `
        <span *ngFor="let error of (control.errors | entries)">
            {{msgString(error[0], error[1])}}
        </span>
    `,
    styles: [``]
})
export class ValidationMessagesComponent
{
    /**
     * The AbstractControl to survey for errors
     */
    @Input()
    public control : AbstractControl;

    /**
     * A trivial function to make {@link ValidationMessages}'s getMessage method
     * available to the template.
     */
    public msgString (errorName : string, value? : any) : string
    {
        return ValidationMessages.getMessage(errorName, value);
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/support/controls/validation-messages.component.ts